import React, { useEffect, useMemo, useState } from "react";
import { App, AutoComplete, Button, Card, Input, Space, Switch, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { LDS_AUTOCMPLETE_CITIES } from "../../../assets/Data/LdsAirportsAutocomplete";
import {
  createAgentSiteDestination,
  deleteAgentSiteDestination,
  fetchAgentSiteDestinations,
  toggleAgentSiteDestination,
  updateAgentSiteDestination,
} from "../../../services/agentSiteDestinationsApi";

const { Text, Title } = Typography;
const { TextArea } = Input;

const EMPTY_FORM = {
  destination_code: "",
  destination_name_he: "",
  destination_name_en: "",
  image_url: "",
  seo_title: "",
  seo_description: "",
  intro_text: "",
  active_on_homepage: true,
  dedicated_page_enabled: true,
  sort_order: 0,
};

export default function AgentSiteDestinationsSettings() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [destinationSearch, setDestinationSearch] = useState("");

  const destinationOptions = useMemo(() => {
    const source = Array.isArray(LDS_AUTOCMPLETE_CITIES) ? LDS_AUTOCMPLETE_CITIES : [];
    const seen = new Set();
    return source
      .map((dest) => {
        const code = String(dest?.AIRPORTCODE || dest?.CITYCODE || "").trim().toUpperCase();
        if (!code || code === "ALL") return null;
        if (seen.has(code)) return null;
        seen.add(code);
        const nameHe = String(dest?.CITYNAME_HEB || "").trim();
        const nameEn = String(dest?.CITYNAME || "").trim();
        return {
          code,
          nameHe,
          nameEn,
          label: `${nameHe || nameEn || code} (${code})`,
        };
      })
      .filter(Boolean);
  }, []);

  const autocompleteOptions = useMemo(() => {
    const term = String(destinationSearch || "").trim().toLowerCase();
    return destinationOptions
      .filter((item) => {
        if (!term) return true;
        return (
          item.code.toLowerCase().includes(term) ||
          item.nameHe.toLowerCase().includes(term) ||
          item.nameEn.toLowerCase().includes(term)
        );
      })
      .slice(0, 100)
      .map((item) => ({
        value: item.code,
        label: item.label,
      }));
  }, [destinationOptions, destinationSearch]);

  const sortedItems = useMemo(
    () => (items || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [items]
  );

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await fetchAgentSiteDestinations();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e?.response?.data?.message || "טעינת יעדים נכשלה");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setDestinationSearch("");
  };

  const onDestinationPick = (code) => {
    const matched = destinationOptions.find((x) => x.code === code);
    setForm((prev) => ({
      ...prev,
      destination_code: code,
      destination_name_he: matched?.nameHe || prev.destination_name_he || "",
      destination_name_en: matched?.nameEn || prev.destination_name_en || "",
    }));
  };

  const validate = () => {
    if (!String(form.destination_code || "").trim()) {
      return "יש לבחור destination_code";
    }
    return null;
  };

  const onSave = async () => {
    const err = validate();
    if (err) {
      message.error(err);
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateAgentSiteDestination(editingId, form);
        setItems((prev) => prev.map((x) => (x.id === editingId ? updated : x)));
        message.success("היעד עודכן");
      } else {
        const created = await createAgentSiteDestination(form);
        setItems((prev) => [...prev, created]);
        message.success("היעד נוסף");
      }
      resetForm();
      await loadItems();
    } catch (e) {
      message.error(e?.response?.data?.message || "שמירת יעד נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      destination_code: item.destination_code || "",
      destination_name_he: item.destination_name_he || "",
      destination_name_en: item.destination_name_en || "",
      image_url: item.image_url || "",
      seo_title: item.seo_title || "",
      seo_description: item.seo_description || "",
      intro_text: item.intro_text || "",
      active_on_homepage: item.active_on_homepage !== false,
      dedicated_page_enabled: item.dedicated_page_enabled !== false,
      sort_order: Number(item.sort_order) || 0,
    });
    setDestinationSearch(item.destination_code || "");
  };

  const onDelete = async (id) => {
    try {
      await deleteAgentSiteDestination(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      if (editingId === id) resetForm();
      message.success("היעד נמחק");
    } catch (e) {
      message.error(e?.response?.data?.message || "מחיקת יעד נכשלה");
    }
  };

  const onToggle = async (id, field) => {
    try {
      const updated = await toggleAgentSiteDestination(id, field);
      setItems((prev) => prev.map((x) => (x.id === id ? updated : x)));
    } catch (e) {
      message.error(e?.response?.data?.message || "עדכון מצב יעד נכשל");
    }
  };

  const onMove = async (item, direction) => {
    const idx = sortedItems.findIndex((x) => x.id === item.id);
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= sortedItems.length) return;
    const a = sortedItems[idx];
    const b = sortedItems[target];
    try {
      await Promise.all([
        updateAgentSiteDestination(a.id, { sort_order: b.sort_order }),
        updateAgentSiteDestination(b.id, { sort_order: a.sort_order }),
      ]);
      await loadItems();
    } catch (e) {
      message.error(e?.response?.data?.message || "שינוי סדר יעדים נכשל");
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="הוספה/עריכה של יעד">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <AutoComplete
            value={destinationSearch}
            options={autocompleteOptions}
            onChange={setDestinationSearch}
            onSelect={onDestinationPick}
            placeholder="חיפוש יעד (ATH / אתונה / Athens)"
            style={{ width: "100%" }}
            filterOption={false}
          />
          <Input
            placeholder="destination_code (לדוגמה ATH)"
            value={form.destination_code}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                destination_code: String(e.target.value || "").trim().toUpperCase(),
              }))
            }
          />
          <Input
            placeholder="שם יעד בעברית"
            value={form.destination_name_he}
            onChange={(e) => setForm((prev) => ({ ...prev, destination_name_he: e.target.value }))}
          />
          <Input
            placeholder="שם יעד באנגלית"
            value={form.destination_name_en}
            onChange={(e) => setForm((prev) => ({ ...prev, destination_name_en: e.target.value }))}
          />
          <Input
            placeholder="URL תמונת יעד"
            value={form.image_url}
            onChange={(e) => setForm((prev) => ({ ...prev, image_url: e.target.value }))}
          />
          <Input
            placeholder="SEO title"
            value={form.seo_title}
            onChange={(e) => setForm((prev) => ({ ...prev, seo_title: e.target.value }))}
          />
          <TextArea
            rows={2}
            placeholder="SEO description"
            value={form.seo_description}
            onChange={(e) => setForm((prev) => ({ ...prev, seo_description: e.target.value }))}
          />
          <TextArea
            rows={4}
            placeholder="טקסט פתיחה לעמוד יעד"
            value={form.intro_text}
            onChange={(e) => setForm((prev) => ({ ...prev, intro_text: e.target.value }))}
          />
          <Space wrap>
            <Switch
              checked={form.active_on_homepage}
              onChange={(value) => setForm((prev) => ({ ...prev, active_on_homepage: value }))}
            />
            <Text>להציג בעמוד הבית</Text>
          </Space>
          <Space wrap>
            <Switch
              checked={form.dedicated_page_enabled}
              onChange={(value) => setForm((prev) => ({ ...prev, dedicated_page_enabled: value }))}
            />
            <Text>להפעיל עמוד ייעודי</Text>
          </Space>
          <Input
            type="number"
            placeholder="sort_order"
            value={form.sort_order}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                sort_order: Number.parseInt(e.target.value || "0", 10) || 0,
              }))
            }
          />
          <Space>
            <Button type="primary" onClick={onSave} loading={saving}>
              {editingId ? "שמור שינויים" : "הוסף יעד"}
            </Button>
            {editingId ? <Button onClick={resetForm}>בטל עריכה</Button> : null}
          </Space>
        </Space>
      </Card>

      <Card title="יעדים קיימים" loading={loading}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          {sortedItems.map((item, idx) => (
            <Card key={item.id} size="small">
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.destination_name_he || item.destination_code}
                    style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 6 }}
                  />
                ) : (
                  <div
                    style={{
                      width: 120,
                      height: 80,
                      borderRadius: 6,
                      background: "#f3f4f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#6b7280",
                      fontSize: 12,
                    }}
                  >
                    ללא תמונה
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 220 }}>
                  <Title level={5} style={{ margin: 0 }}>
                    {item.destination_name_he || item.destination_name_en || item.destination_code}
                  </Title>
                  <Text type="secondary">
                    {item.destination_code} • slug: {item.destination_slug}
                  </Text>
                  <div>
                    <Text type={item.active_on_homepage ? "success" : "warning"}>
                      {item.active_on_homepage ? "מוצג בעמוד הבית" : "לא מוצג בעמוד הבית"}
                    </Text>
                  </div>
                  <div>
                    <Text type={item.dedicated_page_enabled ? "success" : "warning"}>
                      {item.dedicated_page_enabled ? "עמוד ייעודי פעיל" : "עמוד ייעודי כבוי"}
                    </Text>
                  </div>
                </div>
                <Space wrap>
                  <Button
                    icon={<ArrowUpOutlined />}
                    disabled={idx === 0}
                    onClick={() => onMove(item, "up")}
                  />
                  <Button
                    icon={<ArrowDownOutlined />}
                    disabled={idx === sortedItems.length - 1}
                    onClick={() => onMove(item, "down")}
                  />
                  <Button onClick={() => onToggle(item.id, "active_on_homepage")}>
                    {item.active_on_homepage ? "הסתר מהבית" : "הצג בבית"}
                  </Button>
                  <Button onClick={() => onToggle(item.id, "dedicated_page_enabled")}>
                    {item.dedicated_page_enabled ? "כבה עמוד ייעודי" : "הפעל עמוד ייעודי"}
                  </Button>
                  <Button icon={<EditOutlined />} onClick={() => onEdit(item)} />
                  <Button danger icon={<DeleteOutlined />} onClick={() => onDelete(item.id)} />
                </Space>
              </div>
            </Card>
          ))}
          {sortedItems.length === 0 ? <Text type="secondary">עדיין לא הוגדרו יעדים.</Text> : null}
        </Space>
      </Card>
    </Space>
  );
}
